import { Button, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import Loader from "components/Loader";
import Table, { ColumnType } from "components/Table";
import { snack } from "components/toast";
import { useConfirm } from "context/ConfirmDialog";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ResType } from "types";
import { handleError } from "utils/handleError";
import {
  getDisabledGstatClients,
  enableGstatClient,
  enableBulkGstatClients,
  exportDisabledGstatClients,
} from "api/services/gstat/config";
import SearchContainer from "components/SearchContainer";
import { getCurrentFormattedDateTime, getTitle } from "utils";
import { useUserData } from "context/UserProfile";
import { usePermissions } from "context/PermissionsProvider";
import { Permissions } from "data/permissons";
import PermissionDenied from "components/PemissionDenied";
import { SortDirection } from "data/constants";
import _ from "lodash";
import sortHandler from "components/Table/SortHandler";
import moment from "moment";
import excelIcon from "./../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import ValidateAccess from "components/ValidateAccess";

function DeletedGstatClients() {
  const [selected, setSelected] = useState<any[]>([]);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const { data: userData } = useUserData();
  const { permissions } = usePermissions();
  const [sort, setSort] = useState<any>({});

  const { data, isLoading }: ResType = useQuery(
    [
      "disabled-gstat",
      { limit: pageCount, offset: page * pageCount, search, sort },
    ],
    getDisabledGstatClients,
    {
      onSuccess: (res) => {
        setDeletedUsers(res?.data?.result);
        setTotalCount(res?.data?.count);
      },
      onError: (err: any) => {
        if (!err?.error) {
          snack.error("An error occurred while fetching disabled GSTAT clients");
        }
      },
    }
  );

  const { mutate: exportMutate, isLoading: isExporting } = useMutation(
    exportDisabledGstatClients,
    {
      onSuccess: (res: any) => {
        const arr = new Uint8Array(res.data?.data);
        const blob = new Blob([arr], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const file = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        const formattedDateTime = getCurrentFormattedDateTime();
        link.href = file;
        link.download = `ATOM Pro - Disabled GSTAT Clients ${formattedDateTime}.xlsx`;
        link.click();
        snack.success("Exported successfully");
      },
      onError: (err: any) => {
        snack.error(handleError(err));
      },
    }
  );

  const handleExport = () => {
    if (isExporting) return;
    exportMutate({ search });
  };

  const { mutate } = useMutation(enableGstatClient, {
    onSuccess: () => {
      snack.success("Successfully ENABLED Atom Pro for GSTAT client.");
      queryClient.invalidateQueries("disabled-gstat");
    },
    onError: (err: any) => {
      if (err && !err?.error) {
        snack.error(handleError(err));
      } else if (!err?.error) {
        snack.error("An error occurred while enabling GSTAT client");
      }
    },
  });

  const { mutate: bulkMutate } = useMutation(enableBulkGstatClients, {
    onSuccess: () => {
      setSelected([]);
      snack.success("Successfully ENABLED Atom Pro for GSTAT clients.");
      queryClient.invalidateQueries("disabled-gstat");
    },
    onError: (err: any) => {
      if (err && !err?.error) {
        snack.error(handleError(err));
      } else if (!err?.error) {
        snack.error("An error occurred while enabling GSTAT clients");
      }
    },
  });

  const handleRestore = (id: number) => {
    confirm({
      msg: "Are you sure you want to ENABLE GSTAT Client from Atom Pro?",
      action: () => mutate(id),
    });
  };

  const handleBulkEnable = () => {
    confirm({
      msg: "Are you sure you want to ENABLE GSTAT Clients from Atom Pro?",
      action: () => {
        bulkMutate({ gstatClients: selected });
      },
    });
  };

  const defaultColumns: Array<ColumnType> = [
    {
      key: "client.clientId",
      title: "Client ID",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "client.clientNumber",
      title: "Client #",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "userName",
      title: "GSTIN",
      render: (row) => <Typography>{row?.userName}</Typography>,
    },
    {
      key: "category",
      title: "Category",
      render: (rowData) => getTitle(rowData?.client?.newCategory?.name),
    },
    {
      key: "client.displayName",
      title: "Client Name",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
    },
    {
      key: "updatedAt",
      title: "Last Updated at",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row) => moment(row?.updatedAt).format("DD-MM-YYYY HH:mm"),
    },
  ];

  if (
    userData?.role?.name === "Admin" ||
    permissions.includes(Permissions.ENABLE_DELETED_GST_CLIENTS)
  ) {
    defaultColumns.push({
      key: "Action",
      title: "Action",
      render: (item: any) => (
        <ValidateAccess name={Permissions.ENABLE_DISABLE}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => handleRestore(item?.id)}
          >
            ENABLE
          </Button>
        </ValidateAccess>
      ),
    });
  }

  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns,
    });
  };

  if (isLoading) return <Loader />;

  if (
    userData?.role?.name !== "Admin" &&
    !permissions.includes(Permissions.VIEW_DELETED_GST_CLIENTS)
  ) {
    return <PermissionDenied />;
  }

  return (
    <Box>
      <Box
        display="flex"
        gap={3}
        justifyContent="space-between"
        mt={2}
        mb={1}
        px={1}
      >
        <SearchContainer
          debounced
          value={search}
          placeHolder="Search by Client Name | GSTIN"
          onChange={setSearch}
        />
        <Box ml={2} display="flex">
          <ValidateAccess name={Permissions.ENABLE_DISABLE}>
            {selected.length > 0 &&
              (userData?.role?.name === "Admin" ||
                permissions.includes(
                  Permissions.ENABLE_DELETED_GST_CLIENTS
                )) && (
                <Box>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ mr: 1, mt: "5px" }}
                    onClick={handleBulkEnable}
                  >
                    Enable
                  </Button>
                </Box>
              )}
          </ValidateAccess>
          <Button>
            <Button>
              <Tooltip
                title={isExporting ? "Exporting..." : "Data Export to Excel"}
              >
                <img
                  src={excelIcon}
                  alt="Export"
                  style={{
                    cursor: isExporting ? "not-allowed" : "pointer",
                    width: 35,
                    height: 35,
                    marginTop: -10,
                    opacity: isExporting ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                  onClick={handleExport}
                />
              </Tooltip>
            </Button>
          </Button>
        </Box>
      </Box>
      <Box px={1}>
        <Table
          sx={{ mb: 0, pb: 0, height: "520px" }}
          loading={isLoading}
          data={deletedUsers || []}
          selection={{ selected, setSelected }}
          columns={columns}
          sortHandler={handleSort}
          pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
        />
      </Box>
    </Box>
  );
}

export default DeletedGstatClients;
